import React, { useMemo } from 'react';
import { Box, Button, Chip, Grid, IconButton, Paper, Tooltip, Typography, useTheme } from '@mui/material';
import { Clear, Info } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AccountData, ResourceCalculationMode, ResourceHoldData, SchedulerConfigurationData } from '../../../../../api';
import { useAuth } from '../../../../../session/InternalAuthProvider';
import { useLanguage } from '../../../../../common/GeneralUtils';
import { localizedLabel, maskToIndexes, resourceDisplayName, resourceShortName } from '../../../../../utils/maskUtils';
import {
  countFree,
  ResourceAvailability,
  ResourceAvailabilityMap,
  resourceAvailabilityForSpan,
} from '../../../../utils/resourceAvailability';
import { useOrders } from '../../../../swr/useOrders';
import { useUnavailabilities } from '../../../../swr/useUnavailabilities';

type ResourceSelectionPanelProps = {
  orderPageId: string;
  persons: number;
  selectedHolds: ResourceHoldData[];
  setSelectedHolds: (holds: ResourceHoldData[]) => void;
  disabled?: boolean;
  /** The booking's slot. Without it the panel cannot know what is taken, and says so. */
  date?: string;
  startSeconds?: number;
  durationSeconds?: number;
  /** Excluded from occupancy so the order does not appear to clash with itself. */
  excludeOrderId?: string;
  /** Tables the scheduler currently seats the order on, shown while no hold is chosen. */
  scheduledIndexes: number[];
};

type PickerArea = {
  key: string;
  label: string;
  indexes: number[];
};

export const ResourceSelectionPanel: React.FC<ResourceSelectionPanelProps> = ({
  orderPageId,
  persons,
  selectedHolds,
  setSelectedHolds,
  disabled = false,
  date,
  startSeconds,
  durationSeconds,
  excludeOrderId,
  scheduledIndexes,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;

  const configs = useMemo(() => account?.shared_resources?.scheduler_configs ?? [], [account]);
  const pageConfig = configs.find((config) => config.allowed_masks?.some((mask) => mask.order_page_id === orderPageId));

  const hasSlot = Boolean(date && startSeconds !== undefined && durationSeconds);
  const { items: ordersByDate } = useOrders({ startDate: date ?? '', endDate: date ?? '' });
  const { items: unavailabilitiesByDate } = useUnavailabilities({ startDate: date ?? '', endDate: date ?? '' });

  /** What each table is doing over this booking's span, per config: free, taken, or blocked. */
  const availabilityByConfig = useMemo(() => {
    const byConfig: Record<string, ResourceAvailabilityMap> = {};
    if (!hasSlot || !date) {
      return byConfig;
    }
    configs.forEach((config) => {
      byConfig[config.id] = resourceAvailabilityForSpan({
        config,
        orders: ordersByDate[date] ?? [],
        unavailabilities: unavailabilitiesByDate[date] ?? [],
        startSeconds: startSeconds as number,
        endSeconds: (startSeconds as number) + (durationSeconds as number),
        excludeOrderId,
      });
    });
    return byConfig;
  }, [hasSlot, configs, date, ordersByDate, unavailabilitiesByDate, startSeconds, durationSeconds, excludeOrderId]);

  const areasOf = (config: SchedulerConfigurationData): PickerArea[] => {
    const areas = config.areas ?? [];
    if (areas.length === 0) {
      return [
        {
          key: config.id,
          label: localizedLabel(config.label, language),
          indexes: maskToIndexes('1'.repeat(config.resource_count)),
        },
      ];
    }
    return areas.map((area) => ({
      key: `${config.id}-${area.id}`,
      label: localizedLabel(area.label, language),
      indexes: maskToIndexes(area.mask),
    }));
  };

  // A static page books a fixed number of resources whatever the party size (a group booking
  // locks the whole room), so the persons formula does not describe it.
  const staticResources = pageConfig?.allowed_masks?.find(
    (mask) => mask.order_page_id === orderPageId && mask.resource_calculation_mode === ResourceCalculationMode.STATIC,
  )?.static_resources;

  const resourcesNeeded = staticResources ?? (pageConfig ? Math.ceil(persons / pageConfig.persons_per_resource) : 0);

  // While no hold is chosen, the scheduler's seating shows as the selection, so picking a table starts from it.
  const heldIndexes = (configId: string): number[] => {
    if (selectedHolds.length === 0) {
      return configId === pageConfig?.id ? scheduledIndexes : [];
    }
    return selectedHolds.find((hold) => hold.scheduler_config_id === configId)?.resource_indexes ?? [];
  };

  const setIndexesForConfig = (configId: string, indexes: number[]) => {
    const otherHolds = selectedHolds.filter((hold) => hold.scheduler_config_id !== configId);
    if (indexes.length === 0) {
      setSelectedHolds(otherHolds);
      return;
    }
    const sortedIndexes = [...indexes].sort((first, second) => first - second);
    const existingHold = selectedHolds.find((hold) => hold.scheduler_config_id === configId);
    setSelectedHolds(
      existingHold
        ? selectedHolds.map((hold) =>
            hold.scheduler_config_id === configId ? { ...hold, resource_indexes: sortedIndexes } : hold,
          )
        : [...otherHolds, { scheduler_config_id: configId, resource_indexes: sortedIndexes }],
    );
  };

  const toggleTable = (configId: string, index: number) => {
    if (disabled) return;
    const current = heldIndexes(configId);
    setIndexesForConfig(
      configId,
      current.includes(index) ? current.filter((held) => held !== index) : [...current, index],
    );
  };

  const selectableIndexesOf = (availability: ResourceAvailabilityMap | undefined, areaIndexes: number[]): number[] =>
    availability
      ? areaIndexes.filter((index) => availability[index]?.type !== ResourceAvailability.OCCUPIED)
      : areaIndexes;

  const toggleWholeArea = (configId: string, selectableIndexes: number[]) => {
    if (disabled) return;
    const current = heldIndexes(configId);
    const isWholeAreaSelected = selectableIndexes.every((index) => current.includes(index));
    setIndexesForConfig(
      configId,
      isWholeAreaSelected
        ? current.filter((held) => !selectableIndexes.includes(held))
        : [...current, ...selectableIndexes.filter((index) => !current.includes(index))],
    );
  };

  const handleClearSelection = () => {
    if (disabled) return;
    setSelectedHolds([]);
  };

  const unavailableTooltip = (availability: ResourceAvailabilityMap | undefined, index: number) => {
    const info = availability?.[index];
    if (!info || info.type === ResourceAvailability.FREE) {
      return undefined;
    }
    if (info.type === ResourceAvailability.BLOCKED) {
      return t('calendar.edit_order.resourceSelection.blocked');
    }
    return info.orderNumbers.length > 0
      ? t('calendar.edit_order.resourceSelection.takenByOrders', { orders: info.orderNumbers.join(', ') })
      : t('calendar.edit_order.resourceSelection.taken');
  };

  if (configs.length === 0 || persons === 0) {
    return null;
  }

  const selectedCount = selectedHolds.reduce((sum, hold) => sum + hold.resource_indexes.length, 0);

  return (
    <Box>
      {pageConfig && (
        <Chip
          size="small"
          icon={<Info />}
          label={t('calendar.edit_order.resourceSelection.needsResources', {
            count: resourcesNeeded,
            persons,
          })}
          variant="outlined"
          sx={{ mb: 2 }}
        />
      )}

      {scheduledIndexes.length > 0 && selectedHolds.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {t('calendar.edit_order.resourceSelection.currentlySeated', {
            resources: scheduledIndexes.map((index) => resourceShortName(index, pageConfig, language)).join(', '),
          })}
        </Typography>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        {configs.map((config, configIndex) => {
          const availability = availabilityByConfig[config.id];
          const heldInConfig = heldIndexes(config.id);
          return (
            <Box key={config.id} sx={{ mb: configIndex === configs.length - 1 ? 0 : 2 }}>
              {configs.length > 1 && (
                <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                  {localizedLabel(config.label, language)}
                </Typography>
              )}
              {areasOf(config).map((area) => (
                <Box key={area.key} sx={{ mb: 1.5 }}>
                  <Grid container alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {area.label}
                      {availability && (
                        <>
                          {area.label ? ' · ' : ''}
                          {t('calendar.edit_order.resourceSelection.freeOfTotal', {
                            count: countFree(availability, area.indexes),
                            total: area.indexes.length,
                          })}
                        </>
                      )}
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      clickable
                      disabled={disabled}
                      label={t('calendar.edit_order.resourceSelection.wholeArea')}
                      onClick={() => toggleWholeArea(config.id, selectableIndexesOf(availability, area.indexes))}
                    />
                  </Grid>
                  <Grid container spacing={1}>
                    {area.indexes.map((index) => {
                      const info = availability?.[index];
                      const isSelected = heldInConfig.includes(index);
                      const isTaken = !isSelected && Boolean(info) && info?.type !== ResourceAvailability.FREE;
                      const isSelectedAndOccupied = isSelected && info?.type === ResourceAvailability.OCCUPIED;
                      const showTaken = isTaken || isSelectedAndOccupied;
                      const isDisabled = disabled || isTaken;
                      const isBlocked = info?.type === ResourceAvailability.BLOCKED;
                      const takenColor = isSelectedAndOccupied
                        ? theme.palette.error.main
                        : isBlocked
                        ? theme.palette.mb.control
                        : theme.palette.error.light;
                      const fullName = resourceDisplayName(index, config, language);
                      const shortName = resourceShortName(index, config, language);
                      const takenTextColor = isSelectedAndOccupied
                        ? theme.palette.error.contrastText
                        : isBlocked
                        ? theme.palette.text.secondary
                        : theme.palette.error.main;
                      return (
                        <Grid item key={index}>
                          <Tooltip
                            title={[
                              shortName !== fullName ? fullName : '',
                              showTaken ? unavailableTooltip(availability, index) ?? '' : '',
                            ]
                              .filter(Boolean)
                              .join(' · ')}>
                            <span>
                              <Button
                                variant={isSelected ? 'contained' : 'outlined'}
                                size="small"
                                disabled={isDisabled}
                                onClick={() => toggleTable(config.id, index)}
                                sx={{
                                  minWidth: 40,
                                  px: 1,
                                  ...(showTaken && {
                                    textDecoration: isBlocked ? 'none' : 'line-through',
                                    '&, &.Mui-disabled': {
                                      backgroundColor: takenColor,
                                      borderColor: takenColor,
                                      color: takenTextColor,
                                    },
                                  }),
                                }}>
                                {shortName}
                              </Button>
                            </span>
                          </Tooltip>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              ))}
            </Box>
          );
        })}
      </Paper>

      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          <Typography variant="body2" color="text.secondary">
            {selectedCount > 0
              ? t('calendar.edit_order.resourceSelection.selectedCount', { count: selectedCount })
              : t('calendar.edit_order.resourceSelection.autoAssign')}
          </Typography>
        </Grid>

        {selectedCount > 0 && (
          <Grid item>
            <IconButton
              size="small"
              onClick={handleClearSelection}
              disabled={disabled}
              title={t('calendar.edit_order.resourceSelection.clearSelection')}>
              <Clear fontSize="small" />
            </IconButton>
          </Grid>
        )}
      </Grid>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        {t('calendar.edit_order.resourceSelection.manualHint')}
      </Typography>
    </Box>
  );
};
